import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useMaterialReactTable, MaterialReactTable} from 'material-react-table';
import {Box, Typography, Chip, Button, IconButton, Avatar, Tooltip, Link} from '@mui/material';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import apiService from '../../services/apiService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../../components/ConfirmDialog';
import {ROLES} from '../../config/roles';
import Protected from '../../components/Protected';
import {isRenewableConfig} from './geltConfig';

const GeltsPage = () => {
    const navigate = useNavigate();
    const [gelts, setGelts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const fetchGelts = useCallback(() => {
        setLoading(true);
        apiService.get('/api/gelts')
            .then(data => setGelts(data))
            .catch(err => setError(err.message || 'Failed to fetch gelts'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchGelts();
    }, [fetchGelts]);

    const handleDeleteItem = async () => {
        try {
            await apiService.delete(`/api/gelts/${itemToDelete.id}`);
            fetchGelts();
        } catch (err) {
            setError('Failed to delete gelt.');
        } finally {
            setConfirmOpen(false);
            setItemToDelete(null);
        }
    };

    const columns = useMemo(() => [
        {
            accessorKey: 'id',
            header: 'ID',
            size: 50,
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/gelts/${cell.getValue()}`}>
                    {cell.getValue()}
                </Link>
            ),
        },
        {
            accessorKey: 'iconUrl',
            header: 'Icon',
            size: 60,
            Cell: ({cell, row}) => (
                <Avatar src={cell.getValue()} alt={row.original.name}/>
            ),
        },
        {
            accessorKey: 'identifier',
            header: 'Identifier',
            size: 120,
        },
        {
            accessorKey: 'name',
            header: 'Name',
            Cell: ({cell, row}) => (
                <Link component={RouterLink} to={`/gelts/${row.original.id}`}>
                    {cell.getValue()}
                </Link>
            ),
        },
        {
            accessorKey: 'minigameName',
            header: 'Minigame',
            size: 120,
            Cell: ({cell}) => cell.getValue() || 'Universal',
        },
        {
            id: 'renewable',
            header: 'Renewable',
            size: 100,
            Cell: ({row}) => (
                <Chip
                    label={isRenewableConfig(row.original) ? 'Yes' : 'No'}
                    color={isRenewableConfig(row.original) ? 'success' : 'default'}
                    size="small"
                />
            ),
        },
        {
            accessorKey: 'regenMax',
            header: 'Max',
            size: 80,
            Cell: ({cell}) => cell.getValue() ?? '-',
        },
        {
            accessorKey: 'sortOrder',
            header: 'Sort Order',
            size: 80,
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data: gelts,
        enablePagination: false,
        enableBottomToolbar: false,
        state: {isLoading: loading, showAlertBanner: !!error},
        muiToolbarAlertBannerProps: error ? {color: 'error', children: `Error: ${error}`} : undefined,
        initialState: {
            showGlobalFilter: true,
            sorting: [{id: 'sortOrder', desc: false}],
        },
        renderTopToolbarCustomActions: () => (
            <Protected component={GeltsPage} action="create">
                <Button variant="contained" startIcon={<AddIcon/>} onClick={() => navigate('/gelts/new')}>
                    Create Gelt
                </Button>
            </Protected>
        ),
        enableRowActions: true,
        displayColumnDefOptions: {'mrt-row-actions': {header: 'Actions', size: 100}},
        positionActionsColumn: 'last',
        renderRowActions: ({row}) => (
            <Box sx={{display: 'flex', gap: '0.5rem'}}>
                <Tooltip title="Edit">
                    <IconButton component={RouterLink} to={`/gelts/${row.original.id}`}>
                        <EditIcon/>
                    </IconButton>
                </Tooltip>
                <Protected component={GeltsPage} action="delete">
                    <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => {
                            setItemToDelete(row.original);
                            setConfirmOpen(true);
                        }}>
                            <DeleteIcon/>
                        </IconButton>
                    </Tooltip>
                </Protected>
            </Box>
        ),
    });

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>Gelts</Typography>
            <MaterialReactTable table={table}/>
            <Protected component={GeltsPage} action="delete">
                <ConfirmDialog
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    onConfirm={handleDeleteItem}
                    title={`Delete Gelt: ${itemToDelete?.name}?`}
                    message="Are you sure you want to delete this gelt? This action cannot be undone."
                />
            </Protected>
        </Box>
    );
};

GeltsPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    create: [ROLES.ADMINS],
    delete: [ROLES.ADMINS],
};

export default GeltsPage;
